import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn } from './ui/FadeIn';
import { ChevronDown, Clock, CheckCircle2, AlertCircle, FileText, List, MessageSquare, Calendar, Shield, Lightbulb, Target, TrendingUp } from 'lucide-react';
interface Bonus {
  id: number;
  title: string;
  subtitle: string;
  value: string;
  image: string;
  promise: string;
  problem: string;
  solution: string;
  includes: {
    icon: React.ReactNode;
    title: string;
    description: string;
  }[];
  tables?: {
    title: string;
    headers: string[];
    rows: string[][];
  }[];
  keyTakeaway: string;
}
export function BonusesSection() {
  const [openBonus, setOpenBonus] = useState<number | null>(null);
  const bonuses: Bonus[] = [{
    id: 1,
    title: 'Kit S.O.S. "Calma en 5 Minutos"',
    subtitle: '(Noche 1)',
    value: '$27',
    image: 'https://cdn.magicpatterns.com/uploads/1DpDakt4gD7m1PathpanHD/bono_1.png',
    promise: 'En 5 minutos vas a bajar la intensidad del llanto y salir del modo pánico — sin improvisar, sin encender todas las luces, sin cambiar seis cosas a la vez.',
    problem: 'Hay noches en que el sistema falla. El bebé llora más fuerte de lo normal. Tú estás más agotada de lo normal. Y el plan que tenías en la cabeza desaparece por completo.',
    solution: 'Para esas noches existe el Kit S.O.S. No es un método. Es un protocolo de emergencia nocturna. Cinco pasos. Un minuto cada uno. Diseñado para ejecutarse cuando ya no puedes pensar.',
    includes: [{
      icon: <List className="w-5 h-5" data-id="element-14" />,
      title: 'Protocolo 5×1 completo',
      description: 'Chequeo → Reset sensorial → Contacto correcto → Ritmo → Transición. Cada paso explicado en términos simples, con qué hacer Y qué no hacer.'
    }, {
      icon: <Target className="w-5 h-5" data-id="element-15" />,
      title: 'Mapa de decisiones "Si pasa X, hago Y"',
      description: 'Si se calma en brazos pero llora al acostarlo → esto. Si se arquea con el movimiento → esto. Si tú estás al límite → esto. Sin ramificaciones mentales. Sin adivinar. Solo ejecutar.'
    }, {
      icon: <Shield className="w-5 h-5" data-id="element-16" />,
      title: 'Frases ancla para ti — no para el bebé',
      description: '"Esto es intensidad, no peligro." "Un paso. No toda la noche." "Yo marco el ritmo." Una frase correcta a las 3 a.m. puede bajar tu cortisol en segundos.'
    }, {
      icon: <FileText className="w-5 h-5" data-id="element-17" />,
      title: 'Resumen imprimible — 1 hoja',
      description: 'Para pegar en la pared. En la oscuridad. Sin buscar en el teléfono. Lista cuando la necesites.'
    }],
    keyTakeaway: '"Lo levanto / lo acuesto" 20 veces en una noche no es persistencia. Es improvisación en bucle. El Kit S.O.S. te saca del bucle.'
  }, {
    id: 2,
    title: 'Guión Anti-Discusión Nocturna',
    subtitle: 'Para mamá y papá',
    value: '$27',
    image: 'https://cdn.magicpatterns.com/uploads/1698vGu3CzJPfDTgbPA7vx/bono_2.png',
    promise: 'Evita peleas a medianoche con frases exactas y un acuerdo simple para coordinarse en 2 minutos y volver a dormir — sin drama, sin culpas, sin "tú nunca".',
    problem: 'El sueño fragmentado no solo cansa el cuerpo. También rompe la coordinación de pareja. A las 3 de la mañana, con semanas de déficit de sueño encima, el cerebro de ambos está en modo supervivencia.',
    solution: 'Este bono no es terapia de pareja. Es logística nocturna. Roles pre-definidos. Guiones listos. Una palabra clave que lo cambia todo. Sin conversaciones largas. Sin negociaciones a medianoche. Solo coordinación.',
    includes: [{
      icon: <FileText className="w-5 h-5" data-id="element-18" />,
      title: 'Mini eBook PDF (12–18 páginas)',
      description: 'Los 3 roles nocturnos, por qué discutimos cuando hay sueño fragmentado, y cómo evitarlo desde esta noche.'
    }, {
      icon: <CheckCircle2 className="w-5 h-5" data-id="element-19" />,
      title: 'Worksheet "Acuerdo Nocturno en 7 Minutos"',
      description: 'Complétalo esta noche antes de dormir. Define quién interviene primero, quién da soporte, quién toma el relevo — y cuál es la palabra clave de pausa.'
    }, {
      icon: <AlertCircle className="w-5 h-5" data-id="element-20" />,
      title: 'La palabra clave "RESET"',
      description: 'Una sola palabra que significa: "Estoy al límite. Toma tú 10 minutos. Sin preguntas. Sin debate." Practicada de día, funciona de noche aunque los dos estén agotados.'
    }, {
      icon: <MessageSquare className="w-5 h-5" data-id="element-21" />,
      title: '8 guiones listos — copy/paste',
      description: 'Para usar en la oscuridad. Antes de dormir, cuando hay desacuerdo, cuando el cansancio explota, y más.'
    }],
    tables: [{
      title: 'Lista Roja: Frases prohibidas a las 3 a.m.',
      headers: ['Frase que destruye', 'Frase que construye'],
      rows: [['"Tú nunca…"', '"Ahora necesito que hagas X."'], ['"Siempre haces lo mismo."', '"Hoy probemos esto y mañana ajustamos."'], ['"No sirve."', '"Hagamos el siguiente paso y lo medimos."'], ['"Te lo dije."', '"Estoy contigo. Terminemos este momento."'], ['"Estás haciéndolo mal."', '"¿Quieres que te releve 10 minutos?"']]
    }],
    keyTakeaway: 'De nada sirve tener el protocolo perfecto si a mitad de la noche hay un cortocircuito de comunicación entre los dos. Roles claros + guiones listos + palabra de pausa = menos caos, más coordinación, más sueño para ambos.'
  }, {
    id: 3,
    title: 'Planner de Siestas "Aterrizaje Suave"',
    subtitle: '(0–12 meses)',
    value: '$37',
    image: 'https://cdn.magicpatterns.com/uploads/d2gXgCUUc6pkHN5R2eomZ7/bono_3.png',
    promise: 'Ordena las siestas con un sistema flexible para que tu bebé llegue a la noche mejor regulado — y los despertares bajen de intensidad sin rutinas imposibles.',
    problem: 'La noche no se arregla solo en la noche. Si tu bebé llega al momento de dormir sobre-cansado — con el sistema nervioso disparado, con demasiado estímulo encima, con una siesta que salió mal — ningún protocolo nocturno va a poder compensarlo del todo.',
    solution: 'Este planner arregla eso. No es una rutina rígida. No te pide horas exactas ni condiciones perfectas. Es un sistema de lectura de señales + estructura flexible para que el día apoye la noche — aunque cada día sea distinto.',
    includes: [{
      icon: <TrendingUp className="w-5 h-5" data-id="element-22" />,
      title: 'Semáforo de señales de sueño',
      description: 'Verde (sigue despierto), Amarillo (ya se acerca), Rojo (pasado de rosca). Cuando está en ROJO, no "aprietes" más. Usa el plan de rescate.'
    }, {
      icon: <Target className="w-5 h-5" data-id="element-23" />,
      title: 'Framework "Ancla + Ajuste"',
      description: 'Elige UNA siesta clave del día para proteger (el Ancla). El resto son bloques flexibles que se ajustan al día real — no al día ideal.'
    }, {
      icon: <Calendar className="w-5 h-5" data-id="element-24" />,
      title: 'Plantillas diarias y semanales',
      description: 'Imprimibles + versión rellenable para móvil. En 5 días tienes datos. En 7 días, ajustes. Sin adivinar.'
    }, {
      icon: <AlertCircle className="w-5 h-5" data-id="element-25" />,
      title: 'Guía "Rescate de día difícil"',
      description: 'Día de vacunas. Visita que trastocó todo. Siesta que no ocurrió. Ya está resuelto — solo ejecutas.'
    }, {
      icon: <Lightbulb className="w-5 h-5" data-id="element-26" />,
      title: '10 ejemplos rellenados',
      description: 'Por edad y escenario: siestas cortas, transiciones, días de vacunas, viajes, y más.'
    }],
    keyTakeaway: 'La noche no se arregla solo en la noche. Se arregla en el día. Y este planner es el puente entre los dos.'
  }];
  return <section className="py-24 bg-white" data-id="element-27">
      <div className="max-w-6xl mx-auto px-6" data-id="element-28">
        <FadeIn data-id="element-29">
          <div className="text-center mb-16" data-id="element-30">
            <h2 className="text-3xl md:text-5xl font-bold text-brown-dark mb-4" data-id="element-31">
              3 Bonos Adicionales Completamente Gratis
            </h2>
            <p className="text-xl text-brown/80 font-serif max-w-3xl mx-auto" data-id="element-32">
              Junto con la Guía de Sueño Express, recibes 3 bonos adicionales
              que por sí solos valen más que el precio total que vas a pagar.
            </p>
          </div>
        </FadeIn>

        <div className="space-y-6" data-id="element-33">
          {bonuses.map((bonus, index) => <FadeIn key={bonus.id} delay={index * 0.1} data-id="element-34">
              <div className={`bg-cream/30 rounded-3xl border-2 transition-all duration-300 overflow-hidden ${openBonus === bonus.id ? 'border-accent shadow-soft' : 'border-cream-dark hover:border-accent/50'}`} data-id="element-35">
                {/* Header */}
                <button onClick={() => setOpenBonus(openBonus === bonus.id ? null : bonus.id)} className="w-full p-6 md:p-8 text-left focus:outline-none" aria-expanded={openBonus === bonus.id} data-id="element-36">
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-6" data-id="element-37">
                    {/* Image */}
                    <div className="flex-shrink-0 w-32 h-40 md:w-40 md:h-48 rounded-2xl overflow-hidden shadow-md" data-id="element-38">
                      <img src={bonus.image} alt={bonus.title} className="w-full h-full object-cover" data-id="element-39" />
                    </div>

                    {/* Content */}
                    <div className="flex-1" data-id="element-40">
                      <div className="flex items-start justify-between gap-4 mb-3" data-id="element-41">
                        <div data-id="element-42">
                          <div className="inline-block bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-bold mb-2" data-id="element-43">
                            BONO #{bonus.id} — GRATIS
                          </div>
                          <h3 className="text-2xl md:text-3xl font-bold text-brown-dark" data-id="element-44">
                            {bonus.title}
                          </h3>
                          <p className="text-lg text-brown/70 font-serif" data-id="element-45">
                            {bonus.subtitle}
                          </p>
                        </div>
                        <ChevronDown className={`w-6 h-6 text-accent flex-shrink-0 transition-transform duration-300 ${openBonus === bonus.id ? 'rotate-180' : ''}`} data-id="element-46" />
                      </div>

                      <p className="text-brown-dark/80 leading-relaxed mb-4" data-id="element-47">
                        {bonus.promise}
                      </p>
                    </div>
                  </div>
                </button>

                {/* Expanded Content */}
                <AnimatePresence data-id="element-48">
                  {openBonus === bonus.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-49">
                      <div className="px-6 md:px-8 pb-8 space-y-8" data-id="element-50">
                        {/* Problem */}
                        <div className="bg-white/60 rounded-2xl p-6" data-id="element-51">
                          <h4 className="font-bold text-brown-dark mb-3 flex items-center gap-2" data-id="element-52">
                            <AlertCircle className="w-5 h-5 text-accent" data-id="element-53" />
                            El problema
                          </h4>
                          <p className="text-brown-dark/80 leading-relaxed" data-id="element-54">
                            {bonus.problem}
                          </p>
                        </div>

                        {/* Solution */}
                        <div className="bg-accent/5 rounded-2xl p-6" data-id="element-55">
                          <h4 className="font-bold text-brown-dark mb-3 flex items-center gap-2" data-id="element-56">
                            <CheckCircle2 className="w-5 h-5 text-accent" data-id="element-57" />
                            La solución
                          </h4>
                          <p className="text-brown-dark/80 leading-relaxed" data-id="element-58">
                            {bonus.solution}
                          </p>
                        </div>

                        {/* What's Included */}
                        <div data-id="element-59">
                          <h4 className="font-bold text-brown-dark mb-4 text-xl" data-id="element-60">
                            Lo que incluye:
                          </h4>
                          <div className="grid md:grid-cols-2 gap-4" data-id="element-61">
                            {bonus.includes.map((item, idx) => <div key={idx} className="bg-white rounded-xl p-5 border border-cream-dark" data-id="element-62">
                                <div className="flex items-start gap-3" data-id="element-63">
                                  <div className="flex-shrink-0 w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center text-accent" data-id="element-64">
                                    {item.icon}
                                  </div>
                                  <div data-id="element-65">
                                    <h5 className="font-bold text-brown-dark mb-1" data-id="element-66">
                                      {item.title}
                                    </h5>
                                    <p className="text-sm text-brown-dark/70 leading-relaxed" data-id="element-67">
                                      {item.description}
                                    </p>
                                  </div>
                                </div>
                              </div>)}
                          </div>
                        </div>

                        {/* Tables */}
                        {bonus.tables && bonus.tables.length > 0 && <div data-id="element-68">
                            {bonus.tables.map((table, tableIdx) => <div key={tableIdx} className="mb-6" data-id="element-69">
                                <h4 className="font-bold text-brown-dark mb-4" data-id="element-70">
                                  {table.title}
                                </h4>
                                <div className="overflow-x-auto" data-id="element-71">
                                  <table className="w-full bg-white rounded-xl overflow-hidden border border-cream-dark" data-id="element-72">
                                    <thead data-id="element-73">
                                      <tr className="bg-accent/10" data-id="element-74">
                                        {table.headers.map((header, hIdx) => <th key={hIdx} className="px-4 py-3 text-left font-bold text-brown-dark" data-id="element-75">
                                            {header}
                                          </th>)}
                                      </tr>
                                    </thead>
                                    <tbody data-id="element-76">
                                      {table.rows.map((row, rIdx) => <tr key={rIdx} className="border-t border-cream-dark" data-id="element-77">
                                          {row.map((cell, cIdx) => <td key={cIdx} className="px-4 py-3 text-brown-dark/80" data-id="element-78">
                                              {cell}
                                            </td>)}
                                        </tr>)}
                                    </tbody>
                                  </table>
                                </div>
                              </div>)}
                          </div>}

                        {/* Key Takeaway */}
                        <div className="bg-brown-dark text-white rounded-2xl p-6" data-id="element-79">
                          <div className="flex items-start gap-3" data-id="element-80">
                            <Lightbulb className="w-6 h-6 flex-shrink-0 mt-1" data-id="element-81" />
                            <p className="text-lg leading-relaxed italic" data-id="element-82">
                              {bonus.keyTakeaway}
                            </p>
                          </div>
                        </div>

                        {/* Value Reminder */}
                        <div className="text-center pt-4 border-t border-cream-dark" data-id="element-83">
                          <p className="text-accent font-bold text-lg" data-id="element-84">
                            Hoy lo recibes GRATIS con tu compra
                          </p>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </FadeIn>)}
        </div>

        {/* Total Value */}
        <FadeIn delay={0.4} data-id="element-85">
          
        </FadeIn>

        {/* Complete Checklist */}
        <FadeIn delay={0.5} data-id="element-86">
          <div className="mt-16 bg-white rounded-3xl shadow-soft p-8 md:p-12 border-2 border-brown-dark" data-id="element-87">
            <h3 className="text-2xl md:text-3xl font-bold text-brown-dark text-center mb-8" data-id="element-88">
              Todo lo que recibes hoy:
            </h3>

            <div className="space-y-8" data-id="element-89">
              {/* Main Guide */}
              <div data-id="element-90">
                <h4 className="font-bold text-brown-dark mb-4 text-xl flex items-center gap-2" data-id="element-91">
                  <CheckCircle2 className="w-6 h-6 text-accent" data-id="element-92" />
                  Guía de Sueño Express para Bebés
                </h4>
                <ul className="grid md:grid-cols-2 gap-x-6 gap-y-3 ml-8" data-id="element-93">
                  {['PDF eBook completo con instrucciones paso a paso', 'Lista de verificación para identificar patrones de despertares', 'Hoja de trabajo para evaluar métodos anteriores', 'El marco del Método de Micro-pausas con sus tres fases', 'Lista de verificación del entorno óptimo de sueño', 'Guía horaria detallada para las primeras 24 horas', 'Tablas de ajuste por edad (0-3, 4-7, 8-12 meses)', 'Lista de verificación de ajustes mensuales', 'Mini-guía de audio con afirmaciones calmantes', 'Plantilla imprimible de registro de sueño del bebé'].map((item, idx) => <li key={idx} className="flex items-start gap-2 text-brown-dark/80" data-id="element-94">
                      <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-1" data-id="element-95" />
                      <span data-id="element-96">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Bonus 1 */}
              <div className="border-t border-cream-dark pt-6" data-id="element-97">
                <h4 className="font-bold text-brown-dark mb-4 text-xl flex items-center gap-2" data-id="element-98">
                  <CheckCircle2 className="w-6 h-6 text-accent" data-id="element-99" />
                  BONO #1: Kit S.O.S. "Calma en 5 Minutos"
                  <span className="text-sm font-normal text-accent ml-2" data-id="element-100">
                    (Valor: $27)
                  </span>
                </h4>
                <ul className="grid md:grid-cols-2 gap-x-6 gap-y-3 ml-8" data-id="element-101">
                  {['Protocolo 5×1 completo paso a paso', 'Mapa de decisiones "Si pasa X, hago Y"', 'Frases ancla para bajar tu cortisol', 'Resumen imprimible de 1 hoja', '5 tarjetas rápidas (móvil y printable)'].map((item, idx) => <li key={idx} className="flex items-start gap-2 text-brown-dark/80" data-id="element-102">
                      <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-1" data-id="element-103" />
                      <span data-id="element-104">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Bonus 2 */}
              <div className="border-t border-cream-dark pt-6" data-id="element-105">
                <h4 className="font-bold text-brown-dark mb-4 text-xl flex items-center gap-2" data-id="element-106">
                  <CheckCircle2 className="w-6 h-6 text-accent" data-id="element-107" />
                  BONO #2: Guión Anti-Discusión Nocturna
                  <span className="text-sm font-normal text-accent ml-2" data-id="element-108">
                    (Valor: $27)
                  </span>
                </h4>
                <ul className="grid md:grid-cols-2 gap-x-6 gap-y-3 ml-8" data-id="element-109">
                  {['Mini eBook PDF (12–18 páginas)', 'Worksheet "Acuerdo Nocturno en 7 Minutos"', 'La palabra clave "RESET"', 'Lista Roja: Frases prohibidas + reemplazos', '8 guiones listos copy/paste', 'Revisión Express de 2 minutos'].map((item, idx) => <li key={idx} className="flex items-start gap-2 text-brown-dark/80" data-id="element-110">
                      <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-1" data-id="element-111" />
                      <span data-id="element-112">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Bonus 3 */}
              <div className="border-t border-cream-dark pt-6" data-id="element-113">
                <h4 className="font-bold text-brown-dark mb-4 text-xl flex items-center gap-2" data-id="element-114">
                  <CheckCircle2 className="w-6 h-6 text-accent" data-id="element-115" />
                  BONO #3: Planner de Siestas "Aterrizaje Suave"
                  <span className="text-sm font-normal text-accent ml-2" data-id="element-116">
                    (Valor: $37)
                  </span>
                </h4>
                <ul className="grid md:grid-cols-2 gap-x-6 gap-y-3 ml-8" data-id="element-117">
                  {['Semáforo de señales de sueño', 'Framework "Ancla + Ajuste"', 'Plantilla diaria imprimible + móvil', 'Plantilla semanal para ver patrones', 'Guía "Rescate de día difícil"', '10 ejemplos rellenados por edad y escenario'].map((item, idx) => <li key={idx} className="flex items-start gap-2 text-brown-dark/80" data-id="element-118">
                      <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-1" data-id="element-119" />
                      <span data-id="element-120">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            {/* Total Summary */}
            <div className="mt-8 pt-8 border-t-2 border-brown-dark text-center" data-id="element-121">
              <p className="text-lg text-brown-dark/70 mb-2" data-id="element-122">
                Valor total del paquete completo:
              </p>
              <p className="text-4xl md:text-5xl font-bold text-brown-dark mb-4" data-id="element-123">
                <span className="line-through text-brown/40" data-id="element-124">$47</span>
              </p>
              <p className="text-2xl md:text-3xl font-bold text-accent" data-id="element-125">
                Tu precio hoy: Solo $6,39
              </p>
              <p className="text-brown-dark/60 mt-2" data-id="element-126">
                Acceso inmediato a todo el contenido
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}